type CurrentContentParams = {
    contentCollection: any;
    contentEnrollment?: any;
    elementType?: string;
    nodeCode?: any;
    nodeCodeCurrent?: any;
    questionnaire?: any;
};

type EvaluationProgressParams = {
    collection: any;
    hasPerfectGrade: boolean;
    progress: number;
    progressStatus: string;
    questionnaire?: any;
};

type NavigationCollectionParams = {
    currentCollection: any;
    enrollmentObject: any;
    nodeEnrollment: any;
    questionnaire: any;
};

export const getEnrollmentObject = (collection: any, nodeEnrollment: any) =>
    collection.contentEnrollment ||
    collection.questionnaire ||
    (collection.elementType === "NODE" ? nodeEnrollment : null);

const hasProperty = (obj: any, property: string) => Object.prototype.hasOwnProperty.call(obj || {}, property);

const isSameContentEnrollment = (left?: any, right?: any) =>
    Boolean(left && right) &&
    left.idContentItem === right.idContentItem &&
    left.idNodeEnrollment === right.idNodeEnrollment;

const isSameQuestionnaire = (left?: any, right?: any) =>
    Boolean(left && right) &&
    left.idQuestionnaire === right.idQuestionnaire &&
    left.idNodeEnrollment === right.idNodeEnrollment;

export const getIsCurrentContent = ({
    contentCollection,
    contentEnrollment,
    elementType,
    nodeCode,
    nodeCodeCurrent,
    questionnaire,
}: CurrentContentParams) => {
    if (contentEnrollment && hasProperty(contentCollection, "contentEnrollment")) {
        const currentContentEnrollment = contentCollection.contentEnrollment || contentCollection.currentItem;

        return isSameContentEnrollment(currentContentEnrollment, contentEnrollment);
    }

    if (questionnaire && hasProperty(contentCollection, "idQuestionnaire")) {
        return isSameQuestionnaire(contentCollection, questionnaire);
    }

    if (contentEnrollment && hasProperty(contentCollection, "objScorm")) {
        return isSameContentEnrollment(contentCollection.rootContent, contentEnrollment);
    }

    if (contentEnrollment && hasProperty(contentCollection, "currentContentCollection")) {
        const currentContentCollection = contentCollection.currentContentCollection;

        if (currentContentCollection.contentEnrollment) {
            return isSameContentEnrollment(currentContentCollection.contentEnrollment, contentEnrollment);
        }

        if (hasProperty(currentContentCollection, "objScorm")) {
            return isSameContentEnrollment(currentContentCollection.rootContent, contentEnrollment);
        }
    }

    if (questionnaire && hasProperty(contentCollection, "currentContentCollection")) {
        const currentContentCollection = contentCollection.currentContentCollection;

        if (hasProperty(currentContentCollection, "idQuestionnaire")) {
            return isSameQuestionnaire(currentContentCollection, questionnaire);
        }
    }

    if (elementType === "NODE") {
        return String(nodeCode) === String(nodeCodeCurrent);
    }

    return false;
};

export const getCurrentIcon = (collection: any, idContentType?: any) => {
    if (idContentType) {
        return idContentType;
    }

    if (collection.elementType === "EVALUATION" && collection.evaluationType === "REACTION") {
        return 100;
    }

    return 99;
};

export const buildNavigationCollection = ({
    currentCollection,
    enrollmentObject,
    nodeEnrollment,
    questionnaire,
}: NavigationCollectionParams) => {
    const { contentEnrollment, elementType, idContentType, idElement, label, nodeCode, title } = currentCollection;

    const navigationNodeCode = nodeCode || nodeEnrollment?.node?.nodeCode || nodeEnrollment?.nodeCode || null;
    const navigationNodeEnrollmentId =
        enrollmentObject?.idNodeEnrollment || (!nodeCode ? nodeEnrollment?.idNodeEnrollment : null);
    const idEnrollment =
        enrollmentObject?.idEnrollment || currentCollection.idEnrollment || nodeEnrollment?.idEnrollment || null;
    const idQuestionnaire = questionnaire?.idQuestionnaire || currentCollection.idQuestionnaire || null;
    const evaluationType = currentCollection.evaluationType || questionnaire?.questionnaireType || null;
    const evaluationMasteryScore = currentCollection.evaluationMasteryScore || questionnaire?.masteryScore || null;
    const idEvaluation = currentCollection.idEvaluation || questionnaire?.idEvaluation || idElement;

    return {
        data: {
            contentEnrollment,
            nodeEnrollment: elementType === "NODE" ? nodeEnrollment : null,
            questionnaire,
            contentLocation: null,
            title,
            label,
            idContentType,
            idEnrollment,
            idNodeEnrollment: navigationNodeEnrollmentId || null,
            idQuestionnaire,
            evaluationType,
            evaluationMasteryScore,
            nodeCode: navigationNodeCode,
        },
        elementType,
        idContentType,
        idBlockElement: null,
        idElement,
        idEvaluation,
        idEnrollment,
        idNodeEnrollment: navigationNodeEnrollmentId || null,
        nodeCode: navigationNodeCode,
        prerequisitesMet: null,
    };
};

export const getProgressLabel = (collection: any, progress: number) =>
    progress ? (collection.editable === 1 ? "Em andamento" : `${Math.round(progress)}%`) : "Não iniciado";

export const getEvaluationProgressTexts = ({
    collection,
    hasPerfectGrade,
    progress,
    progressStatus,
    questionnaire,
}: EvaluationProgressParams) => {
    if (collection.elementType !== "EVALUATION" || collection.evaluationType !== "KNOWLEDGE") {
        const progressRef = collection?.elementType === "NODE" && !collection?.nodeEnrollment ? '0%' 
                            : collection?.elementType === "NODE" && collection?.nodeEnrollment ?  
                                String(collection?.nodeEnrollment?.progress) + "%" : progressStatus;
        return {
            concluidedText: "",
            progressStatus: `<span class='${String(progressRef).includes("100") ? "progressStatusSuccess" : ""}'>${progressRef}</span>`,
        };
    }

    const maxTries = Number(collection.maxTries);

    if (!questionnaire) {
        return {
            concluidedText: `0 de ${maxTries} tentativa${maxTries > 1 ? "s" : ""}`,
            progressStatus,
        };
    }

    const grade = Number(questionnaire?.grade ?? progress ?? 0);
    const masteryScore = Number(questionnaire.masteryScore);
    const attempts = Number(questionnaire.attempts);
    const lockAfterApproval = Number(questionnaire?.lockAfterApproval || 0);
    const attemptsRemaining = Math.max(maxTries - attempts, 0);
    const hasAttemptsRemaining = maxTries !== 1 && attempts < maxTries;
    const hasReachedMasteryScore = grade >= masteryScore;
    const lockAfterApprovalStatus = lockAfterApproval && hasReachedMasteryScore;

    let concluidedText = "";
    let nextProgressStatus = progressStatus;

    if (!questionnaire?.finishDate || grade === null || grade === undefined || attempts === 0) {
        concluidedText = `0 de ${maxTries} tentativa${maxTries > 1 ? "s" : ""}`;
    } else {
        const resultText = hasReachedMasteryScore ? "Nota necessária atingida." : "Nota não atingida.";
        const attemptsText = hasAttemptsRemaining
            ? `Você tem ${attemptsRemaining} tentativa${attemptsRemaining > 1 ? "s": ''} para melhorar a sua nota.`
            : "Todas as tentativas foram utilizadas.";
        const progressStatusClass = hasReachedMasteryScore ? "progressStatusSuccess" : "progressStatusFailed";

        concluidedText = `<span class='${progressStatusClass}'>${resultText} ${
            !lockAfterApprovalStatus ? attemptsText : ""
        }</span>`;
    }

    if (questionnaire?.finishDate && questionnaire?.grade !== null && questionnaire?.grade !== undefined) {
        const gradeText = `${hasPerfectGrade ? "" : "Nota: "}${questionnaire.grade}`;

        nextProgressStatus =
            questionnaire.questionnaireType === "KNOWLEDGE"
                ? `<span class='${hasReachedMasteryScore ? "progressStatusSuccess" : "progressStatusFailed"}'>${gradeText}</span>`
                : `${gradeText}`;
    } else {
        nextProgressStatus = "Não iniciado";
    }

    return {
        concluidedText,
        progressStatus: nextProgressStatus,
    };
};
